import { useEffect, useMemo, useRef, useState } from 'react'
import TeamHeader from '../TeamHeader/TeamHeader'

import './ClientTicketMetrics.css'
import { apiRequest, errorMessage, formatDuration } from './supportApi'
import { AUTO_CLOSED, PRIORITIES, StaffMeta, StaffTicket, SURVEY_LABELS } from './clientTicketTypes'

// Everything on this page is computed in the browser from the staff ticket
// list (GET /manage/?view=all, or ?view=department for team leaders).

const BASE = '/portal/api/client-tickets'
const DAY = 86400000

type Range = '30' | '90' | '180' | '365' | 'all'
const RANGES: { key: Range; label: string }[] = [
    { key: '30', label: '30 days' },
    { key: '90', label: '90 days' },
    { key: '180', label: '6 months' },
    { key: '365', label: '1 year' },
    { key: 'all', label: 'All time' },
]

const RESOLUTION_LABEL: Record<string, string> = {
    customer_confirmed: 'Confirmed by the customer',
    verified_by_fnn: 'Verified by FNN',
    closed_by_fnn: 'Closed by FNN without validation',
    auto_closed_no_input: 'Auto-closed: no reply to a question',
    auto_closed_no_validation: 'Auto-closed: fix not confirmed',
}

// ─── Small helpers ────────────────────────────────────────────────────────────

const ms = (iso: string | null) => (iso ? new Date(iso).getTime() : NaN)

function median(values: number[]): number | null {
    const v = values.filter(n => Number.isFinite(n)).sort((a, b) => a - b)
    if (!v.length) return null
    const mid = Math.floor(v.length / 2)
    return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2
}

const pct = (part: number, whole: number) => (whole ? Math.round((part / whole) * 100) : null)
const showPct = (p: number | null) => (p == null ? '—' : `${p}%`)
const showDur = (seconds: number | null) => (seconds == null ? '—' : formatDuration(seconds))

const isResolved = (t: StaffTicket) => t.status === 'Resolved' && !!t.resolved_at
const metSla = (t: StaffTicket) => !t.sla.breached

/** Start of the bucket (Monday-based week, or month) that contains `time`. */
function bucketStart(time: number, unit: 'week' | 'month') {
    const d = new Date(time)
    d.setHours(0, 0, 0, 0)
    if (unit === 'month') { d.setDate(1); return d.getTime() }
    const offset = (d.getDay() + 6) % 7
    d.setDate(d.getDate() - offset)
    return d.getTime()
}

function nextBucket(time: number, unit: 'week' | 'month') {
    const d = new Date(time)
    if (unit === 'month') d.setMonth(d.getMonth() + 1)
    else d.setDate(d.getDate() + 7)
    return d.getTime()
}

function bucketLabel(time: number, unit: 'week' | 'month') {
    const d = new Date(time)
    return unit === 'month'
        ? d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
        : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

interface GroupRow {
    key: string
    total: number
    open: number
    resolved: number
    met: number
    medianSla: number | null
    medianCalendar: number | null
    medianTriage: number | null
    autoClosed: number
    rated: number
    satisfied: number
}

function groupBy(opened: StaffTicket[], resolved: StaffTicket[], keyOf: (t: StaffTicket) => string): GroupRow[] {
    const keys = new Set([...opened, ...resolved].map(keyOf))
    return Array.from(keys).map(key => {
        const o = opened.filter(t => keyOf(t) === key)
        const r = resolved.filter(t => keyOf(t) === key)
        return {
            key,
            total: o.length,
            open: o.filter(t => t.status !== 'Resolved').length,
            resolved: r.length,
            met: r.filter(metSla).length,
            medianSla: median(r.map(t => t.sla.elapsed_seconds)),
            medianCalendar: median(r.map(t => (ms(t.resolved_at) - ms(t.created_at)) / 1000)),
            medianTriage: median(o.filter(t => t.triaged_at).map(t => (ms(t.triaged_at) - ms(t.created_at)) / 1000)),
            autoClosed: r.filter(t => AUTO_CLOSED.includes(t.resolution_type)).length,
            rated: r.filter(t => t.survey).length,
            satisfied: r.filter(t => (t.survey?.rating ?? 0) >= 4).length,
        }
    }).sort((a, b) => b.total + b.resolved - (a.total + a.resolved))
}

// ─── Charts (plain SVG) ───────────────────────────────────────────────────────

interface VolumePoint { label: string; opened: number; resolved: number; backlog: number }

/** Width of an element in CSS pixels, so charts draw at real size (text stays readable). */
function useWidth<T extends HTMLElement>() {
    const ref = useRef<T>(null)
    const [width, setWidth] = useState(760)
    useEffect(() => {
        const el = ref.current
        if (!el) return
        const observer = new ResizeObserver(([entry]) => setWidth(Math.max(280, Math.round(entry.contentRect.width))))
        observer.observe(el)
        return () => observer.disconnect()
    }, [])
    return [ref, width] as const
}

function VolumeChart({ points }: { points: VolumePoint[] }) {
    const [ref, W] = useWidth<HTMLDivElement>()
    const H = 240, padL = 34, padR = 12, padT = 12, padB = 30
    const max = Math.max(1, ...points.flatMap(p => [p.opened, p.resolved, p.backlog]))
    const step = (W - padL - padR) / Math.max(1, points.length)
    const bar = Math.max(2, Math.min(14, step / 3))
    const y = (v: number) => padT + (H - padT - padB) * (1 - v / max)
    const ticks = [0, Math.round(max / 2), max]
    const labelEvery = Math.ceil(points.length / Math.max(2, Math.floor(W / 70)))
    const line = points.map((p, i) => `${padL + step * i + step / 2},${y(p.backlog)}`).join(' ')

    return (
        <div ref={ref} className="tm-chart-box">
        <svg className="tm-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img"
            aria-label="Tickets opened and resolved per period, with the open backlog">
            {ticks.map(t => (
                <g key={t}>
                    <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} className="tm-grid" />
                    <text x={padL - 6} y={y(t) + 4} className="tm-axis" textAnchor="end">{t}</text>
                </g>
            ))}
            {points.map((p, i) => {
                const cx = padL + step * i + step / 2
                return (
                    <g key={i}>
                        <rect x={cx - bar - 1} y={y(p.opened)} width={bar} height={y(0) - y(p.opened)} className="tm-bar-opened">
                            <title>{`${p.label}: ${p.opened} opened`}</title>
                        </rect>
                        <rect x={cx + 1} y={y(p.resolved)} width={bar} height={y(0) - y(p.resolved)} className="tm-bar-resolved">
                            <title>{`${p.label}: ${p.resolved} resolved`}</title>
                        </rect>
                        {i % labelEvery === 0 && (
                            <text x={cx} y={H - 10} className="tm-axis" textAnchor="middle">{p.label}</text>
                        )}
                    </g>
                )
            })}
            <polyline points={line} className="tm-backlog" />
            {points.map((p, i) => (
                <circle key={i} cx={padL + step * i + step / 2} cy={y(p.backlog)} r={2.5} className="tm-backlog-dot">
                    <title>{`${p.label}: ${p.backlog} open at the end`}</title>
                </circle>
            ))}
        </svg>
        </div>
    )
}

function HBar({ value, max, tone = 'accent' }: { value: number; max: number; tone?: string }) {
    return (
        <span className={`tm-hbar is-${tone}`} aria-hidden="true">
            <span style={{ width: `${max ? Math.max(2, (value / max) * 100) : 0}%` }} />
        </span>
    )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ClientTicketMetrics() {
    const [meta, setMeta] = useState<StaffMeta | null>(null)
    const [tickets, setTickets] = useState<StaffTicket[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [range, setRange] = useState<Range>('90')
    const [team, setTeam] = useState('')
    const [priority, setPriority] = useState('')

    useEffect(() => {
        (async () => {
            const m = await apiRequest<StaffMeta>(`${BASE}/manage/meta/`)
            if (!m.ok) { setError(errorMessage(m.data, 'You don’t have access to client tickets.')); return }
            setMeta(m.data)
            const scope = m.data.is_manager || m.data.is_receiver ? 'all'
                : (m.data.led_departments.length || m.data.my_department) ? 'department' : null
            if (!scope) { setError('Metrics are available to the ticket receiver, managers and team members.'); return }
            const list = await apiRequest<StaffTicket[]>(`${BASE}/manage/?view=${scope}`)
            if (!list.ok) { setError(errorMessage(list.data, 'Tickets couldn’t be loaded.')); return }
            setTickets(list.data)
        })()
    }, [])

    const teams = useMemo(() => Array.from(new Set((tickets ?? [])
        .map(t => t.target_department_name).filter((n): n is string => !!n))).sort(), [tickets])

    const m = useMemo(() => {
        if (!tickets) return null
        const now = Date.now()
        const earliest = Math.min(now, ...tickets.map(t => ms(t.created_at)))
        const from = range === 'all' ? earliest : now - Number(range) * DAY

        const scoped = tickets.filter(t =>
            (!team || t.target_department_name === team) &&
            (!priority || (t.priority ?? 'Untriaged') === priority))

        const opened = scoped.filter(t => ms(t.created_at) >= from)
        const resolved = scoped.filter(t => isResolved(t) && ms(t.resolved_at) >= from)
        const openNow = scoped.filter(t => t.status !== 'Resolved')

        // Volume over time
        const unit: 'week' | 'month' = (now - from) / DAY > 190 ? 'month' : 'week'
        const points: VolumePoint[] = []
        for (let b = bucketStart(from, unit); b <= now; b = nextBucket(b, unit)) {
            const end = nextBucket(b, unit)
            points.push({
                label: bucketLabel(b, unit),
                opened: scoped.filter(t => ms(t.created_at) >= b && ms(t.created_at) < end).length,
                resolved: scoped.filter(t => isResolved(t) && ms(t.resolved_at) >= b && ms(t.resolved_at) < end).length,
                backlog: scoped.filter(t => ms(t.created_at) < end && (!isResolved(t) || ms(t.resolved_at) >= end)).length,
            })
        }

        // SLA usage distribution (resolved tickets)
        const bands = [
            { label: 'Under 25%', test: (r: number) => r < 0.25, tone: 'ok' },
            { label: '25–50%', test: (r: number) => r >= 0.25 && r < 0.5, tone: 'ok' },
            { label: '50–75%', test: (r: number) => r >= 0.5 && r < 0.75, tone: 'ok' },
            { label: '75–100%', test: (r: number) => r >= 0.75 && r <= 1, tone: 'soon' },
            { label: 'Over target', test: (r: number) => r > 1, tone: 'late' },
        ].map(b => ({
            ...b,
            count: resolved.filter(t => b.test(t.sla.elapsed_seconds / (t.sla.target_hours * 3600))).length,
        }))

        // How tickets closed
        const closures = Object.keys(RESOLUTION_LABEL).map(key => ({
            key, label: RESOLUTION_LABEL[key],
            count: resolved.filter(t => t.resolution_type === key).length,
            auto: AUTO_CLOSED.includes(key as StaffTicket['resolution_type']),
        }))
        const unlabeled = resolved.filter(t => !t.resolution_type).length

        // Customer satisfaction (ratings for the current resolution of tickets resolved in the range)
        const rated = resolved.filter(t => t.survey)
        const eligible = resolved.filter(t => t.csat_eligible)
        const ratingCounts = [5, 4, 3, 2, 1].map(n => ({ n, count: rated.filter(t => t.survey!.rating === n).length }))
        const comments = rated
            .filter(t => t.survey!.comment)
            .sort((a, b) => ms(b.survey!.created_at) - ms(a.survey!.created_at))
            .slice(0, 6)

        const met = resolved.filter(metSla).length
        const autoClosed = resolved.filter(t => AUTO_CLOSED.includes(t.resolution_type)).length

        return {
            opened, resolved, openNow, points, unit, bands, closures, unlabeled,
            compliance: pct(met, resolved.length),
            medianSla: median(resolved.map(t => t.sla.elapsed_seconds)),
            medianCalendar: median(resolved.map(t => (ms(t.resolved_at) - ms(t.created_at)) / 1000)),
            medianTriage: median(opened.filter(t => t.triaged_at).map(t => (ms(t.triaged_at) - ms(t.created_at)) / 1000)),
            waiting: openNow.filter(t => t.awaiting_customer).length,
            overdue: openNow.filter(t => t.sla.breached).length,
            untriaged: openNow.filter(t => !t.priority || !t.target_department).length,
            autoRate: pct(autoClosed, resolved.length),
            eligibleCount: eligible.length,
            ratedCount: rated.length,
            csatScore: pct(rated.filter(t => t.survey!.rating >= 4).length, rated.length),
            avgRating: rated.length ? rated.reduce((sum, t) => sum + t.survey!.rating, 0) / rated.length : null,
            responseRate: pct(eligible.filter(t => t.survey).length, eligible.length),
            ratingCounts, comments,
            byTeam: groupBy(opened, resolved, t => t.target_department_name ?? 'Not routed'),
            byPriority: groupBy(opened, resolved, t => t.priority ?? 'Untriaged')
                .sort((a, b) => PRIORITIES.indexOf(b.key) - PRIORITIES.indexOf(a.key)),
            byAssignee: groupBy(opened.filter(t => t.assigned_to_name), resolved.filter(t => t.assigned_to_name),
                t => t.assigned_to_name ?? ''),
        }
    }, [tickets, range, team, priority])

    if (error) {
        return (
            <div>
                <TeamHeader title="Ticket Metrics" />
                <div className="customer-directory-body tm-root"><p className="tm-empty">{error}</p></div>
            </div>
        )
    }

    const rangeLabel = RANGES.find(r => r.key === range)!.label.toLowerCase()
    const targets = meta?.sla_target_hours ?? {}

    return (
        <div>
            <TeamHeader title="Ticket Metrics" />
            <div className="customer-directory-body tm-root">
                <header className="tm-top">
                    <h1>Client ticket metrics</h1>
                    <div className="tm-filters">
                        <div className="tm-seg" role="group" aria-label="Time range">
                            {RANGES.map(r => (
                                <button key={r.key} aria-pressed={range === r.key}
                                    className={range === r.key ? 'active' : ''} onClick={() => setRange(r.key)}>
                                    {r.label}
                                </button>
                            ))}
                        </div>
                        {teams.length > 1 && (
                            <select className="tm-select" value={team} onChange={e => setTeam(e.target.value)} aria-label="Team">
                                <option value="">All teams</option>
                                {teams.map(t => <option key={t}>{t}</option>)}
                            </select>
                        )}
                        <select className="tm-select" value={priority} onChange={e => setPriority(e.target.value)} aria-label="Priority">
                            <option value="">All priorities</option>
                            {[...PRIORITIES].reverse().map(p => <option key={p}>{p}</option>)}
                            <option>Untriaged</option>
                        </select>
                    </div>
                </header>

                {!m && <p className="tm-empty">Loading tickets…</p>}

                {m && (
                    <>
                        {/* The headline: one sentence that answers "how are we doing?" */}
                        <section className="tm-lede" aria-live="polite">
                            {m.resolved.length ? (
                                <p>
                                    <strong className={`tm-lede-figure ${m.compliance != null && m.compliance < 80 ? 'is-low' : ''}`}>
                                        {m.compliance}%
                                    </strong>{' '}
                                    of the {m.resolved.length} tickets resolved in the last {rangeLabel} met their SLA.
                                    Half were resolved within <strong>{showDur(m.medianSla)}</strong> of business time.
                                </p>
                            ) : (
                                <p>No tickets were resolved in the last {rangeLabel}{team && ` for ${team}`}.</p>
                            )}
                        </section>

                        <dl className="tm-strip">
                            <div><dt>Opened</dt><dd>{m.opened.length}</dd></div>
                            <div><dt>Resolved</dt><dd>{m.resolved.length}</dd></div>
                            <div><dt>Open now</dt><dd>{m.openNow.length}</dd></div>
                            <div><dt>Overdue now</dt><dd className={m.overdue ? 'is-late' : ''}>{m.overdue}</dd></div>
                            <div><dt>Waiting on customer</dt><dd>{m.waiting}</dd></div>
                            <div><dt>Needs triage</dt><dd>{m.untriaged}</dd></div>
                            <div><dt>Median time to triage</dt><dd>{showDur(m.medianTriage)}</dd></div>
                            <div><dt>Median calendar time to resolve</dt><dd>{showDur(m.medianCalendar)}</dd></div>
                        </dl>

                        <section className="tm-section">
                            <div className="tm-section-head">
                                <h2>Volume</h2>
                                <ul className="tm-legend">
                                    <li><span className="tm-key is-opened" /> Opened</li>
                                    <li><span className="tm-key is-resolved" /> Resolved</li>
                                    <li><span className="tm-key is-backlog" /> Open at the end of each {m.unit}</li>
                                </ul>
                            </div>
                            <VolumeChart points={m.points} />
                        </section>

                        <div className="tm-two">
                            <section className="tm-section">
                                <div className="tm-section-head">
                                    <h2>How much of the SLA was used</h2>
                                </div>
                                <p className="tm-note">Resolved tickets, by share of their target spent. Paused time, weekends and holidays don’t count.</p>
                                <ul className="tm-bars">
                                    {m.bands.map(b => (
                                        <li key={b.label}>
                                            <span className="tm-bars-label">{b.label}</span>
                                            <HBar value={b.count} max={Math.max(...m.bands.map(x => x.count))} tone={b.tone} />
                                            <span className="tm-bars-value">{b.count}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>

                            <section className="tm-section">
                                <div className="tm-section-head">
                                    <h2>How tickets were closed</h2>
                                </div>
                                <p className="tm-note">
                                    {m.autoRate != null
                                        ? `${m.autoRate}% closed automatically because the customer went quiet. ${m.eligibleCount} were eligible for the satisfaction survey.`
                                        : 'No tickets resolved in this range.'}
                                </p>
                                <ul className="tm-bars">
                                    {m.closures.map(c => (
                                        <li key={c.key}>
                                            <span className="tm-bars-label">{c.label}</span>
                                            <HBar value={c.count} max={Math.max(1, ...m.closures.map(x => x.count))} tone={c.auto ? 'muted' : 'accent'} />
                                            <span className="tm-bars-value">{c.count}</span>
                                        </li>
                                    ))}
                                    {m.unlabeled > 0 && (
                                        <li>
                                            <span className="tm-bars-label">Not recorded (closed before tracking)</span>
                                            <HBar value={m.unlabeled} max={Math.max(1, ...m.closures.map(x => x.count))} tone="muted" />
                                            <span className="tm-bars-value">{m.unlabeled}</span>
                                        </li>
                                    )}
                                </ul>
                            </section>
                        </div>

                        <section className="tm-section">
                            <div className="tm-section-head"><h2>Customer satisfaction</h2></div>
                            {m.ratedCount === 0 ? (
                                <p className="tm-note">
                                    No ratings yet for tickets resolved in this range. Customers are asked to rate tickets
                                    they confirmed, or that FNN verified.
                                </p>
                            ) : (
                                <div className="tm-two">
                                    <div>
                                        <dl className="tm-csat">
                                            <div>
                                                <dt>Satisfied (rated 4 or 5)</dt>
                                                <dd className={(m.csatScore ?? 0) >= 80 ? 'is-ok' : (m.csatScore ?? 0) >= 60 ? 'is-soon' : 'is-late'}>
                                                    {showPct(m.csatScore)}
                                                </dd>
                                            </div>
                                            <div><dt>Average rating</dt><dd>{m.avgRating!.toFixed(1)} <small>/ 5</small></dd></div>
                                            <div><dt>Response rate</dt><dd>{showPct(m.responseRate)} <small>{m.ratedCount} of {m.eligibleCount}</small></dd></div>
                                        </dl>
                                        <ul className="tm-bars">
                                            {m.ratingCounts.map(r => (
                                                <li key={r.n}>
                                                    <span className="tm-bars-label">{r.n}: {SURVEY_LABELS[r.n]}</span>
                                                    <HBar value={r.count} max={Math.max(1, ...m.ratingCounts.map(x => x.count))}
                                                        tone={r.n >= 4 ? 'ok' : r.n === 3 ? 'soon' : 'late'} />
                                                    <span className="tm-bars-value">{r.count}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div>
                                        <h3 className="tm-subhead">Recent comments</h3>
                                        {m.comments.length === 0 && <p className="tm-note">No comments yet.</p>}
                                        <ul className="tm-comments">
                                            {m.comments.map(t => (
                                                <li key={t.ticket_id} className={`is-${t.survey!.rating >= 4 ? 'good' : t.survey!.rating === 3 ? 'neutral' : 'bad'}`}>
                                                    <p>“{t.survey!.comment}”</p>
                                                    <span>
                                                        {t.survey!.rating}/5 on #{t.ticket_id}
                                                        {t.organization_name ? `, ${t.organization_name}` : ''}
                                                        {t.target_department_name ? ` (${t.target_department_name})` : ''}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </section>

                        <section className="tm-section">
                            <div className="tm-section-head"><h2>By team</h2></div>
                            <div className="tm-table-wrap">
                                <table className="tm-table">
                                    <thead>
                                        <tr>
                                            <th>Team</th><th>Opened</th><th>Open now</th><th>Resolved</th>
                                            <th>Met SLA</th><th>Median SLA time</th><th>Median calendar time</th><th>Auto-closed</th><th>Satisfied</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {m.byTeam.map(r => (
                                            <tr key={r.key}>
                                                <th scope="row">{r.key}</th>
                                                <td>{r.total}</td>
                                                <td>{r.open}</td>
                                                <td>{r.resolved}</td>
                                                <td><ComplianceCell met={r.met} total={r.resolved} /></td>
                                                <td>{showDur(r.medianSla)}</td>
                                                <td>{showDur(r.medianCalendar)}</td>
                                                <td>{r.autoClosed}</td>
                                                <td><CsatCell satisfied={r.satisfied} rated={r.rated} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        <div className="tm-two">
                            <section className="tm-section">
                                <div className="tm-section-head"><h2>By priority</h2></div>
                                <div className="tm-table-wrap">
                                    <table className="tm-table">
                                        <thead>
                                            <tr><th>Priority</th><th>Resolved</th><th>Met SLA</th><th>Median SLA time vs target</th></tr>
                                        </thead>
                                        <tbody>
                                            {m.byPriority.map(r => {
                                                const target = targets[r.key] ? targets[r.key] * 3600 : null
                                                return (
                                                    <tr key={r.key}>
                                                        <th scope="row">{r.key}</th>
                                                        <td>{r.resolved}</td>
                                                        <td><ComplianceCell met={r.met} total={r.resolved} /></td>
                                                        <td className="tm-vs">
                                                            {target && r.medianSla != null && (
                                                                <HBar value={Math.min(r.medianSla, target)} max={target}
                                                                    tone={r.medianSla > target ? 'late' : r.medianSla > target * 0.75 ? 'soon' : 'ok'} />
                                                            )}
                                                            <span>{showDur(r.medianSla)}{target ? ` of ${targets[r.key]}h` : ''}</span>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            <section className="tm-section">
                                <div className="tm-section-head"><h2>By assignee</h2></div>
                                <div className="tm-table-wrap">
                                    <table className="tm-table">
                                        <thead>
                                            <tr><th>Assignee</th><th>Open now</th><th>Resolved</th><th>Met SLA</th><th>Median SLA time</th><th>Satisfied</th></tr>
                                        </thead>
                                        <tbody>
                                            {m.byAssignee.length === 0 && (
                                                <tr><td colSpan={6} className="tm-empty-cell">No assigned tickets in this range.</td></tr>
                                            )}
                                            {m.byAssignee.map(r => (
                                                <tr key={r.key}>
                                                    <th scope="row">{r.key}</th>
                                                    <td>{r.open}</td>
                                                    <td>{r.resolved}</td>
                                                    <td><ComplianceCell met={r.met} total={r.resolved} /></td>
                                                    <td>{showDur(r.medianSla)}</td>
                                                    <td><CsatCell satisfied={r.satisfied} rated={r.rated} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        </div>

                        <p className="tm-foot">
                            “Opened” counts tickets created in the range. Resolution figures count tickets resolved in the range.
                            SLA time is business time with the clock running; calendar time is from submission to resolution.
                            Reopened tickets count once, by their latest resolution. “Satisfied” is the share of survey
                            ratings that were 4 or 5; the number in brackets is how many ratings it’s based on.
                        </p>
                    </>
                )}
            </div>
        </div>
    )
}

function ComplianceCell({ met, total }: { met: number; total: number }) {
    const p = pct(met, total)
    return (
        <span className={`tm-compliance ${p == null ? '' : p >= 90 ? 'is-ok' : p >= 75 ? 'is-soon' : 'is-late'}`}>
            {showPct(p)}
        </span>
    )
}

function CsatCell({ satisfied, rated }: { satisfied: number; rated: number }) {
    if (!rated) return <span className="tm-muted">—</span>
    const p = pct(satisfied, rated)
    return (
        <span className={`tm-compliance ${p! >= 80 ? 'is-ok' : p! >= 60 ? 'is-soon' : 'is-late'}`}>
            {p}% <small className="tm-muted">({rated})</small>
        </span>
    )
}